import React, { useState } from 'react';
import { TrainingRecord, Tool, Employee, Curriculum, CurriculumCompletion } from '../types';
import { Search, GraduationCap, Calendar, Star, TrendingUp, BookOpen, ExternalLink, Save, Plus, Trash2, X, Bell } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { InfoTooltip } from '../components/InfoTooltip';

interface TrainingScoresViewProps {
  trainingRecords: TrainingRecord[];
  curriculumCompletions: CurriculumCompletion[];
  tools: Tool[];
  employees: Employee[];
  curricula: Curriculum[];
  onAddCurriculum: (c: Curriculum) => void;
  onUpdateCurriculum: (c: Curriculum) => void;
  onDeleteCurriculum: (id: string) => void;
  onUpdateTool: (tool: Tool) => void;
  onSendReminder?: (employeeId: string, employeeName: string, employeeEmail: string, curriculumName: string, toolName: string) => Promise<void>;
}

export const TrainingScoresView: React.FC<TrainingScoresViewProps> = ({
  trainingRecords,
  curriculumCompletions,
  tools,
  employees,
  curricula,
  onAddCurriculum,
  onUpdateCurriculum,
  onDeleteCurriculum,
  onSendReminder,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [reminderSent, setReminderSent] = useState<Record<string, string>>({});
  const [sendingReminder, setSendingReminder] = useState<string | null>(null);
  const [searchCurriculum, setSearchCurriculum] = useState('');
  const [trainingTab, setTrainingTab] = useState<'curriculum' | 'assignments'>('curriculum');
  const [filterAudience, setFilterAudience] = useState<'all' | 'user' | 'admin'>('all');

  // Curriculum form (add new)
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newToolId, setNewToolId] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newAudience, setNewAudience] = useState<'user' | 'admin'>('user');

  // Curriculum edit inline
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editToolId, setEditToolId] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAudience, setEditAudience] = useState<'user' | 'admin'>('user');

  // Assignments = one row per (employee, curriculum). Score is on the formation.
  const assignments: Array<{
    id: string;
    employeeId: string;
    employeeName: string;
    employeeEmail: string;
    employeeRole: string;
    curriculumName: string;
    toolName: string;
    status: string;
    completedAt?: string;
    satisfaction?: number;
    proficiency?: number;
  }> = [];
  // Only user-audience curricula are assigned to employees; admin-only are not.
  const userCurricula = curricula.filter((c) => c.audience !== 'admin');
  userCurricula.forEach((curriculum) => {
    const tool = tools.find((t) => t.id === curriculum.toolId);
    if (!tool) return;
    tool.assignedUserIds?.forEach((userId) => {
      const emp = employees.find((e) => e.id === userId);
      if (!emp) return;
      const completion = curriculumCompletions.find(
        (cc) => cc.employeeId === emp.id && cc.curriculumId === curriculum.id
      );
      assignments.push({
        id: `${curriculum.id}-${emp.id}`,
        employeeId: emp.id,
        employeeName: emp.name,
        employeeEmail: emp.email ?? '',
        employeeRole: emp.role ?? '',
        curriculumName: curriculum.name,
        toolName: tool.name,
        status: completion ? 'DONE' : 'PENDING',
        completedAt: completion?.completedAt,
        satisfaction: completion?.satisfaction,
        proficiency: completion?.proficiency,
      });
    });
  });
  // Legacy: add rows for tools with trainingUrl but no curricula (use trainingRecords)
  tools.forEach((tool) => {
    const hasCurricula = curricula.some((c) => c.toolId === tool.id);
    if (hasCurricula) return;
    if (!tool.trainingUrl) return;
    tool.assignedUserIds?.forEach((userId) => {
      const emp = employees.find((e) => e.id === userId);
      if (!emp) return;
      const record = trainingRecords.find((r) => r.toolId === tool.id && r.employeeId === emp.id);
      assignments.push({
        id: `legacy-${tool.id}-${emp.id}`,
        employeeId: emp.id,
        employeeName: emp.name,
        employeeEmail: emp.email ?? '',
        employeeRole: emp.role ?? '',
        curriculumName: tool.name,
        toolName: tool.name,
        status: record ? 'DONE' : 'PENDING',
        completedAt: record?.completedAt,
        satisfaction: record?.satisfaction,
        proficiency: record?.proficiency,
      });
    });
  });

  const filteredAssignments = assignments
    .filter(
      (a) =>
        a.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.toolName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.curriculumName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === 'PENDING' ? -1 : 1;
      return 0;
    });

  const filteredCurricula = curricula
    .filter(
      (c) =>
        c.name.toLowerCase().includes(searchCurriculum.toLowerCase()) ||
        tools.find((t) => t.id === c.toolId)?.name.toLowerCase().includes(searchCurriculum.toLowerCase())
    )
    .filter((c) => {
      if (filterAudience === 'all') return true;
      return (c.audience ?? 'user') === filterAudience;
    });

  const getTeamAvgSatisfaction = (curriculumId: string) => {
    const list = curriculumCompletions.filter((cc) => cc.curriculumId === curriculumId && cc.satisfaction != null);
    if (list.length === 0) return null;
    return list.reduce((acc, c) => acc + (c.satisfaction ?? 0), 0) / list.length;
  };

  const startAdding = () => {
    setIsAdding(true);
    setNewName('');
    setNewUrl('');
    setNewToolId('');
    setNewDescription('');
    setNewAudience('user');
  };

  const cancelAdding = () => {
    setIsAdding(false);
    setNewName('');
    setNewUrl('');
    setNewToolId('');
    setNewDescription('');
  };

  const submitAdd = () => {
    const name = newName.trim();
    if (!name || !newToolId) return;
    onAddCurriculum({
      id: '',
      name,
      url: newUrl.trim() || undefined,
      toolId: newToolId,
      description: newDescription.trim() || undefined,
      audience: newAudience,
    });
    cancelAdding();
  };

  const startEditing = (c: Curriculum) => {
    setEditingId(c.id);
    setEditName(c.name);
    setEditUrl(c.url ?? '');
    setEditToolId(c.toolId);
    setEditDescription(c.description ?? '');
    setEditAudience(c.audience ?? 'user');
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const saveEditing = (c: Curriculum) => {
    onUpdateCurriculum({
      ...c,
      name: editName.trim(),
      url: editUrl.trim() || undefined,
      toolId: editToolId,
      description: editDescription.trim() || undefined,
      audience: editAudience,
    });
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Remove this training from the curriculum?')) onDeleteCurriculum(id);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Tabs: Curriculum | Assigned training (green toggle like portal) */}
      <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-lg w-fit">
        <button
          type="button"
          onClick={() => setTrainingTab('curriculum')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${trainingTab === 'curriculum' ? 'bg-green-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <BookOpen size={16} className="inline mr-1.5 -mt-0.5" />
          Curriculum
          <InfoTooltip text="Manage training content. 'User' trainings are assigned to employees; 'Admin' are internal reference." />
        </button>
        <button
          type="button"
          onClick={() => setTrainingTab('assignments')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${trainingTab === 'assignments' ? 'bg-green-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <GraduationCap size={16} className="inline mr-1.5 -mt-0.5" />
          Assigned training
          <InfoTooltip text="Shows which employees must complete which training and their progress." />
        </button>
      </div>

      {trainingTab === 'curriculum' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center flex-wrap gap-2">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <BookOpen className="text-green-600" size={20} />
              Manage curriculum
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={filterAudience}
                onChange={(e) => setFilterAudience(e.target.value as 'all' | 'user' | 'admin')}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <div className="relative max-w-[180px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchCurriculum}
                  onChange={(e) => setSearchCurriculum(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                />
              </div>
              <button
                onClick={startAdding}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                <Plus size={16} /> Add training
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100 min-h-[200px]">
            {isAdding && (
              <div className="p-4 bg-indigo-50/50 border-b border-indigo-100 space-y-3">
                <div className="grid grid-cols-1 gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Training name *"
                    className="w-full p-2 text-sm border border-indigo-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <select
                    value={newToolId}
                    onChange={(e) => setNewToolId(e.target.value)}
                    className="w-full p-2 text-sm border border-indigo-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">— Select a tool —</option>
                    {tools.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    placeholder="URL (optional)"
                    className="w-full p-2 text-sm border border-indigo-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Description (optional)"
                    className="w-full p-2 text-sm border border-indigo-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Audience</label>
                    <select
                      value={newAudience}
                      onChange={(e) => setNewAudience(e.target.value as 'user' | 'admin')}
                      className="w-full p-2 text-sm border border-indigo-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="user">User (assigned to employees)</option>
                      <option value="admin">Admin (not assigned to employees)</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={submitAdd} disabled={!newName.trim() || !newToolId} className="p-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50" title="Save">
                    <Save size={16} />
                  </button>
                  <button onClick={cancelAdding} className="p-2 border border-slate-300 text-slate-500 rounded hover:bg-slate-50" title="Cancel">
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}
            {filteredCurricula.map((c) => {
              const tool = tools.find((t) => t.id === c.toolId);
              const isEdit = editingId === c.id;
              return (
                <div key={c.id} className="p-4 hover:bg-slate-50 transition-colors">
                  {isEdit ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full p-2 text-sm border border-indigo-200 rounded"
                        placeholder="Name"
                      />
                      <select
                        value={editToolId}
                        onChange={(e) => setEditToolId(e.target.value)}
                        className="w-full p-2 text-sm border border-indigo-200 rounded"
                      >
                        {tools.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        placeholder="URL"
                        className="w-full p-2 text-sm border border-indigo-200 rounded"
                      />
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Description"
                        className="w-full p-2 text-sm border border-indigo-200 rounded"
                      />
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Audience</label>
                        <select
                          value={editAudience}
                          onChange={(e) => setEditAudience(e.target.value as 'user' | 'admin')}
                          className="w-full p-2 text-sm border border-indigo-200 rounded"
                        >
                          <option value="user">User (assigned to employees)</option>
                          <option value="admin">Admin (not assigned to employees)</option>
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => saveEditing(c)} className="p-2 bg-green-600 text-white rounded hover:bg-green-700">
                          <Save size={14} />
                        </button>
                        <button onClick={cancelEditing} className="p-2 border border-slate-300 rounded hover:bg-slate-50">
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => startEditing(c)}>
                        <div className="font-medium text-slate-800 flex items-center gap-2">
                          {c.name}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${(c.audience ?? 'user') === 'admin' ? 'bg-slate-200 text-slate-600' : 'bg-green-100 text-green-700'}`}>
                            {(c.audience ?? 'user') === 'admin' ? 'Admin' : 'User'}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Tool: {tool?.name ?? c.toolId}
                          {c.createdAt && (
                            <>
                              {' · '}
                              <Calendar size={10} className="inline" /> Added on {new Date(c.createdAt).toLocaleDateString()}
                            </>
                          )}
                        </div>
                        {c.url && (
                          <a href={c.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-xs text-indigo-600 hover:underline flex items-center gap-1 mt-1 truncate">
                            <ExternalLink size={10} /> {c.url}
                          </a>
                        )}
                        {c.description && <p className="text-xs text-slate-500 mt-1">{c.description}</p>}
                        {getTeamAvgSatisfaction(c.id) != null && (
                          <p className="text-xs text-indigo-600 mt-1">Team avg. satisfaction: {getTeamAvgSatisfaction(c.id)!.toFixed(1)}/10</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => handleDelete(c.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredCurricula.length === 0 && !isAdding && (
              <EmptyState
                icon={<GraduationCap size={40} />}
                title="No training content yet"
                subtitle="Create a curriculum entry, link it to a tool, and assign it to your team."
                action={{ label: 'Create training', onClick: () => setIsAdding(true) }}
              />
            )}
          </div>
        </div>
      )}

      {trainingTab === 'assignments' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center flex-wrap gap-2">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <GraduationCap className="text-green-600" size={20} />
              Assigned training
            </h3>
            <div className="relative max-w-[200px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>
          </div>
          <div className="overflow-x-auto flex-1 min-h-[200px]">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider font-semibold">
                  <th className="sticky left-0 z-20 bg-slate-50 px-4 py-3 min-w-[170px] shadow-[inset_-1px_0_0_#e2e8f0]">Tool</th>
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Training</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Date</th>
                  <th className="px-4 py-3 text-center">Scores</th>
                  <th className="px-4 py-3 text-center">Last Reminder</th>
                  <th className="px-4 py-3 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAssignments.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState
                        icon={<BookOpen size={40} />}
                        title="No training assignments"
                        subtitle="Link training content to tools, then assign them to employees to see progress here."
                      />
                    </td>
                  </tr>
                ) : (
                  filteredAssignments.map((record) => (
                    <tr key={record.id} className="group hover:bg-slate-50 transition-colors">
                      <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-4 py-3 text-slate-700 font-medium min-w-[170px] shadow-[inset_-1px_0_0_#e2e8f0]">{record.toolName}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{record.employeeName}</div>
                        <div className="text-xs text-slate-500">{record.employeeRole}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 font-medium">{record.curriculumName}</td>
                      <td className="px-4 py-3 text-center">
                        {record.status === 'DONE' ? (
                          <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">Done</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-slate-600">
                        {record.completedAt ? new Date(record.completedAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {record.status === 'DONE' ? (
                          <div className="flex justify-center gap-2 text-xs">
                            <span className="flex items-center gap-0.5" title="Satisfaction">
                              <Star size={10} className="text-amber-400 fill-amber-400" /> {record.satisfaction ?? '–'}
                            </span>
                            {record.proficiency != null && (
                              <>
                                <span className="text-slate-300">|</span>
                                <span className="flex items-center gap-0.5" title="Proficiency">
                                  <TrendingUp size={10} className="text-emerald-500" /> {record.proficiency}
                                </span>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-xs text-slate-500">
                        {reminderSent[record.id]
                          ? new Date(reminderSent[record.id]).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {record.status === 'PENDING' && onSendReminder && (
                          <button
                            onClick={async () => {
                              setSendingReminder(record.id);
                              try {
                                await onSendReminder(record.employeeId, record.employeeName, record.employeeEmail, record.curriculumName, record.toolName);
                                setReminderSent(prev => ({ ...prev, [record.id]: new Date().toISOString() }));
                              } catch (error) {
                                const message = error instanceof Error ? error.message : 'Failed to send reminder.';
                                window.alert(message);
                              } finally {
                                setSendingReminder(null);
                              }
                            }}
                            disabled={sendingReminder === record.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors disabled:opacity-50"
                            title={`Send reminder to ${record.employeeName}`}
                          >
                            <Bell size={12} />
                            {sendingReminder === record.id ? 'Sending...' : 'Remind'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
